import {
	CompareTable,
	Conversation,
	InShort,
	RelatedPages,
	type Turn,
} from "@marketing/components/answer";
import { Article } from "@marketing/components/article";
import {
	DetailGrid,
	marketingHead,
	PageIntro,
	Section,
} from "@marketing/components/blocks";
import {
	type ExampleRequest,
	ExampleRequests,
} from "@marketing/components/ExampleRequests";
import { Closing } from "@marketing/home/Closing";
import { Faq } from "@marketing/home/Faq";
import { createFileRoute } from "@tanstack/react-router";
import { CheckIcon, MinusIcon } from "lucide-react";

const UPDATED = "2026-11-05";

const FAQ = [
	{
		q: "What is an Arabic chatbot for ecommerce?",
		a: "An assistant that talks with shoppers in Arabic, searches the store's catalog and builds their basket. Unlike an FAQ bot, it has to understand how people really ask for products: in their dialect, in Arabizi, mixed with English, with brands spelled several ways.",
	},
	{
		q: "Does it understand Arabizi?",
		a: "Yes. Arabic typed in Latin letters and numbers, such as 7aleeb for milk or 3andkom for “do you have”, is read as Arabic, and the reply follows the shopper's script.",
	},
	{
		q: "Saudi, Gulf or Lebanese Arabic: which does it speak?",
		a: "Saudi and Gulf Arabic first, plus Lebanese Arabic and Modern Standard Arabic. Each store picks the dialect its assistant speaks, and the fixed wording, call lines and suggested replies follow it.",
	},
	{
		q: "Does it understand Egyptian Arabic?",
		a: "It reads Egyptian Arabic in chat, but Egypt is not a launch market: there is no Egyptian phrasebook or Egyptian voice today. If many of your shoppers write in Egyptian Arabic, test them on your catalog before you rely on it.",
	},
	{
		q: "Can it reply in the shopper's script?",
		a: "Yes. Arabic script gets an Arabic reply, Arabizi gets Arabizi, English gets English. A message with no clear language, such as a brand name alone, keeps the language the conversation was in.",
	},
	{
		q: "Can it take voice notes and calls in Arabic?",
		a: "Yes. Voice notes are transcribed and answered like typed messages. On calls it hears and speaks Saudi, Gulf or Lebanese Arabic, and more than 30 other languages.",
	},
	{
		q: "Does it work on WhatsApp?",
		a: "Yes. The same Arabic assistant answers on your WhatsApp Business number with product cards, a basket and a checkout the shopper confirms with a tap, and inside your own app.",
	},
	{
		q: "How do I test an Arabic chatbot before buying it?",
		a: "Run the 20 messages on this page against it, on your own catalog. A good assistant gets the dialect, the products, the budget and the follow-ups right, and never invents a product or a delivery time.",
	},
];

const WHY = [
	{
		title: "Dialects, not textbook Arabic",
		text: "Shoppers write وش عندكم, شو عندكم or شو في. A bot trained on formal Arabic misses what they ask for.",
	},
	{
		title: "Arabizi and mixed languages",
		text: "“3andkom diapers size 4?” is one message in two languages and two scripts. It has to be read as one request.",
	},
	{
		title: "Products, not FAQs",
		text: "Most Arabic chatbots answer opening hours. A shopper wants the right milk, at their branch's price, in the basket.",
	},
	{
		title: "Brands spelled every way",
		text: "Almarai, المراعي and almar3i are the same brand. Search has to find it whichever way it is typed.",
	},
	{
		title: "Halal, enforced",
		text: "In a halal store, alcohol and pork must never appear, whatever the wording. That belongs on the server, not in a prompt.",
	},
	{
		title: "Replies that sound local",
		text: "Fixed phrases approved by native speakers for each dialect keep the tone right, instead of a translation that sounds foreign.",
	},
];

const VERSUS = [
	[
		"What it answers",
		"Fixed questions: opening hours, delivery areas, order status",
		"“Do you have…”, “cheapest…”, “something similar”, “kabsa for ten”",
	],
	[
		"Dialect",
		"Formal Arabic, or one dialect from training data",
		"Saudi, Gulf and Lebanese as people write them, with an approved phrasebook per dialect",
	],
	[
		"Arabizi and mixed messages",
		"Often treated as English, or not understood",
		"Read as Arabic; the reply follows the shopper's script",
	],
	[
		"Products",
		"A link to the store, or a product it guessed",
		"Hybrid keyword and meaning search over the live catalog, each result checked against the message",
	],
	[
		"Prices and stock",
		"The website's price, if any",
		"The shopper's branch, in the retailer's currency",
	],
	[
		"Halal rules",
		"An instruction in the prompt",
		"Products removed on the server before the assistant sees them",
	],
	[
		"Ordering",
		"Hands off to the website",
		"Fills the basket and checkout; orders only on the shopper's confirm",
	],
];

const DIALECT_WORDS = [
	[
		"Saudi (Najdi, Hijazi)",
		"أبي / أبغى",
		"وش عندكم / إيش عندكم",
		"الحين / دحين",
		"Saudi phrasebook replies; a Saudi voice on calls",
	],
	[
		"Emirati",
		"أبا / أبي",
		"شو عندكم",
		"الحين",
		"Gulf phrasebook replies; a Gulf Arabic voice",
	],
	[
		"Kuwaiti",
		"أبي",
		"شنو عندكم",
		"الحين",
		"Read as Gulf Arabic; Gulf phrasebook and voice",
	],
	[
		"Lebanese",
		"بدي",
		"شو عندكن / شو في",
		"هلق",
		"Lebanese phrasebook; a Lebanese speech engine on calls",
	],
	[
		"Egyptian",
		"عايز",
		"عندكم إيه",
		"دلوقتي",
		"Read in chat; no Egyptian phrasebook or voice today",
	],
];

const CHAT: Turn[] = [
	{
		from: "shopper",
		text: "شنو عندكم حليب بدون لاكتوز؟",
		en: "What lactose-free milk do you have? (Kuwaiti)",
	},
	{
		from: "assistant",
		text: "هذا المتوفر عندنا من الحليب بدون لاكتوز:",
		en: "Here is the lactose-free milk we have:",
		note: "Searched the catalog; only lactose-free milk passed the check.",
	},
	{
		from: "shopper",
		text: "fi minh 7ajm akbar?",
		en: "Is there a bigger size? (Arabizi)",
	},
	{
		from: "assistant",
		text: "Eh, hatha el 7ajm el akbar min nafs el no3:",
		en: "Yes, here is the bigger size of the same kind:",
		note: "Replied in Arabizi because the shopper switched to Arabizi.",
	},
	{
		from: "shopper",
		text: "Perfect, add 2 please",
	},
	{
		from: "assistant",
		text: "Added 2 to your basket. Anything else?",
		note: "Replied in English; the basket is in sync with the app's cart.",
	},
];

const DIALECTS: { name: string; chat: boolean; voice: boolean }[] = [
	{ name: "Saudi Arabic", chat: true, voice: true },
	{
		name: "Gulf Arabic (Emirati, Kuwaiti, Qatari, Bahraini, Omani)",
		chat: true,
		voice: true,
	},
	{ name: "Lebanese Arabic", chat: true, voice: true },
	{ name: "Modern Standard Arabic", chat: true, voice: false },
	{ name: "Arabizi (Arabic in Latin letters)", chat: true, voice: false },
];

const TESTS: { group: string; items: ExampleRequest[] }[] = [
	{
		group: "Dialects",
		items: [
			{
				text: "أبغى حليب كامل الدسم",
				en: "Saudi: I want full-fat milk.",
				expect: "Full-fat fresh milk first; not skimmed milk, not milk powder.",
			},
			{
				text: "أبا خبز حق الريوق",
				en: "Emirati: I want bread for breakfast.",
				expect: "Breakfast breads, with the reply in Arabic.",
			},
			{
				text: "بدي لبنة وزيت زيتون",
				en: "Lebanese: I want labneh and olive oil.",
				expect: "Two products, one row each.",
			},
			{
				text: "هل يتوفر لديكم أرز بسمتي؟",
				en: "Formal Arabic: do you have basmati rice?",
				expect: "Basmati rice, answered in clear Arabic.",
			},
			{
				text: "وش عندكم من الشاي؟",
				en: "Saudi: what teas do you have?",
				expect: "A range of tea; not kettles or tea cups.",
			},
			{
				text: "عطني أرخص زيت طبخ",
				en: "Give me the cheapest cooking oil.",
				expect: "Cooking oils only, cheapest first.",
			},
		],
	},
	{
		group: "Arabizi",
		items: [
			{
				text: "3andkom 7aleeb almarai?",
				en: "Do you have Almarai milk?",
				expect: "Read as Arabic, finds Almarai milk, answers in Arabizi.",
			},
			{
				text: "bade shi lal fotour",
				en: "Lebanese: I want something for breakfast.",
				expect: "Breakfast ideas from the catalog, not a refusal.",
			},
			{
				text: "abgha mai sghir 12 7abba",
				en: "I want small water bottles, a pack of 12.",
				expect: "A 12-pack of small bottles.",
			},
			{
				text: "fi 3andkon chips bala gluten?",
				en: "Do you have gluten-free chips?",
				expect: "Only gluten-free chips, or an honest no.",
			},
		],
	},
	{
		group: "Mixed languages",
		items: [
			{
				text: "عندكم diapers size 4؟",
				en: "Arabic and English in one message.",
				expect: "Size 4 diapers.",
			},
			{
				text: "Need 2 كيلو دجاج و rice",
				en: "Two scripts, two products.",
				expect: "Chicken in packs that make 2 kg, and rice.",
			},
			{
				text: "Pepsi ولا Coca-Cola أرخص؟",
				en: "Which is cheaper, Pepsi or Coca-Cola?",
				expect: "Both prices from the shopper's branch, compared.",
			},
		],
	},
	{
		group: "Real shopping",
		items: [
			{
				text: "كبسة لعشرة أشخاص",
				en: "Kabsa for ten people.",
				expect: "The ingredients, each with the pack that covers the amount.",
			},
			{
				text: "سناكات لستة أشخاص بأقل من ١٠٠ ريال",
				en: "Snacks for six, under 100 riyals in total.",
				expect: "A list whose total fits the budget.",
			},
			{
				text: "فيه شي مشابه بس بدون سكر؟",
				en: "Anything similar but sugar-free? (a follow-up)",
				expect: "Remembers the last product and finds its sugar-free kind.",
			},
			{
				text: "أبغى هدية لطفل عمره ست سنين",
				en: "I want a gift for a six-year-old.",
				expect: "Toys or gifts, not food: search must not assume groceries.",
			},
		],
	},
	{
		group: "Hard cases",
		items: [
			{
				text: "بيبسى دايت",
				en: "Diet Pepsi, spelled with ى instead of ي.",
				expect: "Still finds Diet Pepsi.",
			},
			{
				text: "عندكم بيرة؟",
				en: "Do you have beer? (in a halal store)",
				expect: "Says it is not sold, and never shows it.",
			},
			{
				text: "وين طلبي؟",
				en: "Where is my order?",
				expect: "Checks the order or hands over to the team; never invents a delivery time.",
			},
		],
	},
];

const RELATED = [
	{ href: "/saudi-arabia", label: "For Saudi Arabia" },
	{ href: "/uae", label: "For the UAE" },
	{ href: "/lebanon", label: "Lebanese and Arabizi" },
	{ href: "/channels/whatsapp", label: "Arabic chatbot on WhatsApp" },
	{ href: "/channels/voice", label: "Voice ordering in Arabic" },
	{ href: "/how-it-works", label: "How search works" },
];

export const Route = createFileRoute(
	"/_marketing/arabic-chatbot-for-ecommerce",
)({
	head: () =>
		marketingHead({
			title: "Arabic Chatbot for Ecommerce & Grocery | Hephon Commerce",
			description:
				"Arabic chatbot for ecommerce: what it takes to handle Saudi, Emirati and Lebanese dialects, Arabizi and voice notes, with 20 messages to test any vendor.",
			path: "/arabic-chatbot-for-ecommerce",
			breadcrumb: ["Arabic chatbot for ecommerce"],
			faq: FAQ,
			updated: UPDATED,
		}),
	component: ArabicChatbotPage,
});

function ArabicChatbotPage() {
	return (
		<>
			<PageIntro
				kicker="Arabic chatbot for ecommerce"
				title="An Arabic chatbot that shops: Gulf dialects, Arabizi and your real catalog"
				lead="Most Arabic chatbots answer questions. A shopping assistant has to understand how people really ask for products, find them in your catalog, and fill the basket. Here is what that takes, and 20 messages to test any vendor with."
			/>

			<InShort updated={UPDATED}>
				<p>
					An Arabic chatbot for ecommerce is an assistant that talks
					with shoppers in Arabic, searches the store's catalog and
					builds their basket. To work in a Gulf store it has to read
					dialects (Saudi, Emirati, Kuwaiti, Lebanese), Arabizi and
					Arabic mixed with English, find the right product at the
					shopper's branch, and reply in the shopper's own script.
				</p>
				<p>
					Hephon Commerce is one such assistant, for supermarkets and
					grocery retailers in Saudi Arabia and the UAE, in the
					retailer's app, on WhatsApp and by phone. The 20 test
					messages below work on any vendor's chatbot, ours included.
				</p>
			</InShort>

			<Article>
				<h2 id="what">What is an Arabic chatbot for ecommerce?</h2>
				<p>
					It is a chatbot that sells. A shopper writes "عندكم حليب
					المراعي؟" or "3andkom 7aleeb almarai?" and gets the product,
					at their branch's price, with a button to add it to the
					basket. A support bot that answers opening hours and order
					status in Arabic is useful, but it is a different product.
					The test is simple: can it take a shopper from a sentence in
					their dialect to a confirmed basket?
				</p>
				<p>
					That needs three things a generic chatbot lacks: the
					retailer's live catalog with prices and stock per branch, a
					search that understands Arabic as it is typed, and rules
					that hold whatever the shopper writes, such as never showing
					alcohol in a halal store.
				</p>
			</Article>

			<Section
				id="why-title"
				tone="mist"
				title="Why most Arabic chatbots fail in a store."
			>
				<DetailGrid items={WHY} tone="mist" />
			</Section>

			<Article>
				<h2 id="fail">Where generic Arabic bots break, in practice</h2>
				<p>
					Most failures come from one assumption: that Arabic is one
					language with one way of writing it. Shoppers in Riyadh,
					Dubai and Beirut ask for the same milk with different words.
					Brand names arrive in Arabic script, in English, and in
					Arabizi with numbers. Product names in the catalog are often
					English while the request is Arabic. A search that matches
					only exact words, or a model that was only shown formal
					Arabic, returns nothing, or worse, the wrong product.
				</p>
				<p>
					The second failure is answering in the wrong script. A
					shopper who writes Arabizi does not want a reply in Arabic
					script, and one who writes Arabic does not want English.
					Models easily drift into English after the first product
					name. Hephon Commerce weighs each message as a whole and
					keeps the conversation's language when a message has no
					clear one.
				</p>
			</Article>

			<Section
				id="versus-title"
				tone="mist"
				title="A generic Arabic chatbot next to an Arabic shopping assistant."
			>
				<CompareTable
					columns={[
						"",
						"Generic Arabic chatbot",
						"Arabic shopping assistant (Hephon Commerce)",
					]}
					rows={VERSUS}
				/>
			</Section>

			<Section
				id="dialects-title"
				title="Dialects it speaks."
				lead="Saudi and Gulf Arabic lead. Each store picks the dialect its assistant speaks."
			>
				<div className="mk-reveal">
					<table className="w-full text-start text-[15px] sm:text-[16px]">
						<thead>
							<tr className="border-(--mk-line) border-b text-(--mk-muted) text-[14px]">
								<th className="py-3 pe-4 text-start font-medium">
									Dialect
								</th>
								<th className="w-16 py-3 text-center font-medium sm:w-28">
									Chat
								</th>
								<th className="w-16 py-3 text-center font-medium sm:w-28">
									Voice
								</th>
							</tr>
						</thead>
						<tbody>
							{DIALECTS.map((dialect) => (
								<tr
									key={dialect.name}
									className="border-(--mk-line) border-b"
								>
									<td className="py-3.5 pe-4">
										{dialect.name}
									</td>
									<td className="py-3.5 text-center">
										<Support on={dialect.chat} />
									</td>
									<td className="py-3.5 text-center">
										<Support on={dialect.voice} />
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
				<div className="mt-16">
					<CompareTable
						caption="The same request in five dialects: “I want”, “What do you have?” and “now”, and what the assistant does with each."
						columns={[
							"Dialect",
							"I want",
							"What do you have?",
							"Now",
							"What the assistant does",
						]}
						rows={DIALECT_WORDS}
					/>
				</div>
			</Section>

			<Article>
				<h2 id="arabizi">What is Arabizi, and why does it matter?</h2>
				<p>
					Arabizi is Arabic written in Latin letters, with numbers for
					the sounds Latin has no letter for: 3 for ع, 7 for ح, 2 for
					ء, 5 for خ. "7aleeb" is milk, "3andkom" is "do you have",
					"abgha" is "I want". Many shoppers in the Gulf and Lebanon
					type it on their phones every day, and spell the same word
					several ways. An Arabic chatbot that treats Arabizi as
					broken English cannot serve them. Hephon Commerce reads it
					as Arabic, searches with it, and answers in Arabizi when
					that is how the shopper wrote.
				</p>

				<h2 id="voice-notes">Can it handle voice notes?</h2>
				<p>
					Yes. Many Gulf shoppers would rather speak than type, so on
					WhatsApp they send voice notes. Hephon Commerce transcribes
					each note and answers it like a typed message, with the same
					search, basket and rules. Gulf Arabic is heard and spoken
					with ElevenLabs voices, a Saudi voice among them, and
					Lebanese Arabic through a Lebanese speech engine. By voice
					it also speaks more than 30 other languages.
				</p>

				<h2 id="script">Does it reply in the shopper's script?</h2>
				<p>
					Yes, and this is where many bots slip. The reply follows the
					message: Arabic script to Arabic script, Arabizi to Arabizi,
					English to English. The fixed lines it uses, such as
					confirmations and suggested replies, come from a phrasebook
					of approved phrases per dialect that your team can edit.
				</p>
			</Article>

			<Section
				id="chat-title"
				tone="mist"
				title="One chat, three scripts."
				lead="Kuwaiti Arabic, then Arabizi, then English: the reply follows each message."
				center
			>
				<Conversation
					turns={CHAT}
					caption="A sample conversation written for this page to show the behaviour. It is not a transcript of a real shopper."
				/>
			</Section>

			<Article>
				<h2 id="how-to-test">
					How do you test an Arabic chatbot before buying?
				</h2>
				<p>
					Do not judge it on a demo the vendor drives. Ask for a trial
					on your own catalog and send it the messages your shoppers
					send. The 20 below cover dialects, Arabizi, mixed languages,
					real shopping tasks and the hard cases where bots fail.
					Under each message is what a good shopping assistant should
					do.
				</p>
				<p>
					Score each one pass or fail on four points: did it
					understand the request, did it show the right products and
					only those, did it reply in the shopper's script, and did it
					avoid inventing anything. Then repeat the follow-ups
					("something similar", "cheaper", "add all") in the same
					chat, since memory across turns is where many bots break.
				</p>
			</Article>

			<Section
				id="test-title"
				tone="mist"
				title="20 messages to test any Arabic chatbot."
				lead="Send them to any vendor's assistant, on your own catalog. Each one shows what a good shopping assistant should do."
			>
				<div className="space-y-12">
					{TESTS.map((group) => (
						<div key={group.group}>
							<h3 className="mk-h3 mk-reveal">{group.group}</h3>
							<ExampleRequests
								items={group.items}
								className="mt-5"
							/>
						</div>
					))}
				</div>
			</Section>

			<Article>
				<h2 id="our-tests">How we test our own assistant</h2>
				<p>
					Before any change to the prompt, the model or the search
					goes live, Hephon Commerce runs a test suite generated from
					the retailer's own catalog. It covers every department,
					retrieval, the basket, language and script, safety and
					WhatsApp. New languages are tested before they ship, in the
					dialect the store uses. You can also try the public demo
					store, Hephon Market, with about 15,000 products.
				</p>

				<h2 id="not-fit">When Hephon Commerce is not the right fit</h2>
				<ul>
					<li>
						You need an Arabic support bot for questions and
						tickets, not a shopping assistant. A customer-service
						platform fits better.
					</li>
					<li>
						You sell mainly to Egyptian or Maghrebi shoppers. Our
						dialect work is Gulf and Lebanese first.
					</li>
					<li>
						You have no online catalog. The assistant needs your
						products, prices and stock.
					</li>
				</ul>
			</Article>

			<Faq items={FAQ} title="Arabic chatbots, answered." />
			<RelatedPages links={RELATED} />
			<Closing />
		</>
	);
}

function Support({ on }: { on: boolean }) {
	return on ? (
		<>
			<CheckIcon
				className="inline size-5 text-(--mk-signal)"
				aria-hidden
			/>
			<span className="sr-only">Yes</span>
		</>
	) : (
		<>
			<MinusIcon
				className="inline size-5 text-(--mk-faint)"
				aria-hidden
			/>
			<span className="sr-only">No</span>
		</>
	);
}
